import { DashboardShell } from '@/components/dashboard/dashboard-shell'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useTRPC } from '@/trpc/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Building2, Calendar, Plus, Users } from 'lucide-react'
import { DateTime } from 'luxon'
import { useState } from 'react'
import { toast } from 'sonner'
import { OrganizationsDataTable } from './-components/OrganizationsDataTable'
import { organizationsTableColumns } from './-components/organizationsTableColumns'

export const Route = createFileRoute('/dashboard/admin/super_admin/organizations/')({
  component: RouteComponent,
})

function RouteComponent() {
  const api = useTRPC()
  const queryClient = useQueryClient()

  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    type: 'direct_employer' as 'direct_employer' | 'staffing_agency',
    location: '',
    plan: 'free' as 'free' | 'pro' | 'enterprise',
  })

  const { data: organizations = [], isPending } = useQuery({
    ...api.superAdmin.getOrganizations.queryOptions(),
  })

  const { mutate: createOrganization, isPending: isCreatingOrganization } = useMutation({
    ...api.superAdmin.createOrganization.mutationOptions({
      onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: ['superAdmin', 'getOrganizations'] })
        setIsDialogOpen(false)
        setFormData({
          name: '',
          type: 'direct_employer',
          location: '',
          plan: 'free',
        })
        toast.success(`Organization "${data?.name}" created successfully!`)
        queryClient.invalidateQueries({ queryKey: api.superAdmin.getOrganizations.queryKey() })
      },
      onError: (error) => {
        console.error('Failed to create organization:', error)
        toast.error('Failed to create organization. Please try again.')
      },
    }),
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.name.trim()) return

    createOrganization({
      name: formData.name.trim(),
      type: formData.type,
      location: formData.location.trim() || undefined,
      plan: formData.plan,
    })
  }
  const totalEmployees = organizations.reduce(
    (total, org) => total + (org.employees?.length || 0),
    0,
  )
  const activeOrganizations = organizations.length // All organizations are considered active by default
  const newOrganizationsThisMonth = organizations.filter(
    (org) =>
      DateTime.fromJSDate(org.createdAt).month === DateTime.now().month &&
      DateTime.fromJSDate(org.createdAt).year === DateTime.now().year,
  ).length

  if (isPending) {
    return (
      <DashboardShell>
        <div className="flex flex-col gap-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Organizations</h1>
              <p className="text-muted-foreground">Loading organizations...</p>
            </div>
          </div>
        </div>
      </DashboardShell>
    )
  }

  return (
    <DashboardShell>
      <div className="flex flex-col gap-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Organizations</h1>
            <p className="text-muted-foreground">Manage all organizations in the system</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                Add Organization
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Create Organization</DialogTitle>
                <DialogDescription>
                  Add a new organization to the system. Fill in the required details below.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit}>
                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="name" className="text-right">
                      Name *
                    </Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                      className="col-span-3"
                      placeholder="Organization name"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="type" className="text-right">
                      Type *
                    </Label>
                    <Select
                      value={formData.type}
                      onValueChange={(value: 'direct_employer' | 'staffing_agency') =>
                        setFormData((prev) => ({ ...prev, type: value }))
                      }
                    >
                      <SelectTrigger className="col-span-3">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="direct_employer">Direct Employer</SelectItem>
                        <SelectItem value="staffing_agency">Staffing Agency</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="location" className="text-right">
                      Location
                    </Label>
                    <Input
                      id="location"
                      value={formData.location}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, location: e.target.value }))
                      }
                      className="col-span-3"
                      placeholder="City, Country"
                    />
                  </div>
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="plan" className="text-right">
                      Plan
                    </Label>
                    <Select
                      value={formData.plan}
                      onValueChange={(value: 'free' | 'pro' | 'enterprise') =>
                        setFormData((prev) => ({ ...prev, plan: value }))
                      }
                    >
                      <SelectTrigger className="col-span-3">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="free">Free</SelectItem>
                        <SelectItem value="pro">Pro</SelectItem>
                        <SelectItem value="enterprise">Enterprise</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isCreatingOrganization || !formData.name.trim()}>
                    {isCreatingOrganization ? 'Creating...' : 'Create Organization'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Statistics Cards */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Organizations</CardTitle>
              <Building2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{organizations.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Organizations</CardTitle>
              <Building2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{activeOrganizations}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Employees</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalEmployees}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">This Month</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{newOrganizationsThisMonth}</div>
              <p className="text-xs text-muted-foreground">New organizations</p>
            </CardContent>
          </Card>
        </div>

        {/* Organizations Table */}
        <Card>
          <CardHeader>
            <CardTitle>Organizations</CardTitle>
            <CardDescription>A list of all organizations and their details.</CardDescription>
          </CardHeader>
          <CardContent>
            <OrganizationsDataTable columns={organizationsTableColumns} data={organizations} />
          </CardContent>
        </Card>
      </div>
    </DashboardShell>
  )
}
